/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'
import { type FunctionComponent, Fragment } from 'react'

import { EuiEmptyPrompt, EuiFlexGroup, EuiFlexItem, EuiSpacer } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { RoleAssignments } from '@modules/cloud-api/v1/types'
import {
  filterRoleAssignments,
  getAllRoleAssignments,
  getEsKibanaApiAccess,
  isRoleAssignmentsEmpty,
  normaliseCCMRoleAssignments,
  normaliseDeploymentRoleAssignments,
  normaliseProjectRoleAssignments,
} from '@modules/role-assignments-lib'
import AdminProjectRoleAssignmentsSummary from '@modules/role-assignments-admin-components/ProjectRoleAssignmentsSummary'
import AdminCCMRoleAssignmentsSummary from '@modules/role-assignments-admin-components/CCMRoleAssignmentsSummary'
import UserProjectRoleAssignmentsSummary from '@modules/role-assignments-user-components/ProjectRoleAssignmentsSummary'
import UserCCMRoleAssignmentsSummary from '@modules/role-assignments-user-components/CCMRoleAssignmentsSummary'
import type { ProjectType } from '@modules/ui-types/projects'
import { useConfig } from '@modules/cui/ConfigContext'
import type {
  NormalisedResourceRoleAssignment,
  ResourceType,
} from '@modules/role-assignments-lib/types'
import type { MemberOrApiKey } from '@modules/role-assignments-components/ProjectRoleAssignmentsSummary'
import {
  organizationRoleDescriptions,
  roleBadgeLabels,
} from '@modules/role-assignments-lib/messages'
import AllCCMRoleAssignmentsTable from '@modules/role-assignments-user-components/CCMRoleAssignmentsSummary/AllCCMRoleAssignmentsTable'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'

import PermissionsRolePanel from './PermissionsRolePanel'
import AllProjectRoleAssignmentsTable from './AllProjectRoleAssignmentsTable'
import { OrganizationOwnerApiAccess } from './OrganizationOwnerApiAccess'

import type { AllProjectRoleAssignmentsTableItem } from './AllProjectRoleAssignmentsTable'

export type Props = {
  roleAssignments: RoleAssignments
  organizationId: string
  memberOrApiKey: MemberOrApiKey
}

const RolesSummary: FunctionComponent<Props> = ({
  organizationId,
  roleAssignments,
  memberOrApiKey,
}) => {
  const [_, flags] = useFlagsWhenLoaded()
  const isUIAMEnabled = flags.uiamUi
  const isAdminconsole = useConfig('APP_NAME') === 'adminconsole'

  if (isRoleAssignmentsEmpty(roleAssignments)) {
    const emptyText =
      memberOrApiKey === 'api-key' ? (
        <Fragment>
          <strong>
            <FormattedMessage
              id='apikeys.apikey-role-assignments.prompt-text'
              defaultMessage="This API key doesn't have any role"
            />
          </strong>

          <EuiSpacer size='m' />

          <FormattedMessage
            id='apiKeys.apikey-role-assignments.prompt-text-secondary'
            defaultMessage="You can't add roles to an existing key. You can delete this one, and create a new key instead."
          />
        </Fragment>
      ) : (
        <FormattedMessage
          id='organization.view-permissions-flyout.no-role-assignment'
          defaultMessage='This member does not have a role yet.'
        />
      )

    return <EuiEmptyPrompt titleSize='xs' title={<h4>{emptyText}</h4>} />
  }

  const allRoleAssignmentsTableItems = getAllCloudResourceRoleAssignmentsTableItems(
    roleAssignments,
    memberOrApiKey === 'api-key',
  )
  const specificRoleAssignments = getSpecificCloudResourceRoleAssignments(roleAssignments)

  const ccmClusterAllRoleItems = getCcmClusterAllRoleItems(roleAssignments)

  const ccmClusterSpecificRoleAssignments = filterRoleAssignments(
    normaliseCCMRoleAssignments(roleAssignments.cloud_connected_resource),
    'specificRoleAssignments',
  )

  return (
    <EuiFlexGroup direction='column' gutterSize='s' alignItems='stretch'>
      {roleAssignments.organization?.map((roleAssignment) => {
        const { role_id: roleId } = roleAssignment
        return (
          <EuiFlexItem data-test-id='organization-panel' key={roleId}>
            <PermissionsRolePanel
              title={<FormattedMessage {...roleBadgeLabels[roleId]} />}
              description={<FormattedMessage {...organizationRoleDescriptions[roleId]} />}
            >
              {roleId === 'organization-admin' && isUIAMEnabled && (
                <OrganizationOwnerApiAccess
                  isApiKey={memberOrApiKey === 'api-key'}
                  orgRoleAssignment={roleAssignment}
                />
              )}
            </PermissionsRolePanel>
          </EuiFlexItem>
        )
      })}

      {(allRoleAssignmentsTableItems.length > 0 || specificRoleAssignments.length > 0) && (
        <EuiFlexItem>
          <PermissionsRolePanel
            title={
              <FormattedMessage
                id='roles-summary.instance-access'
                defaultMessage='Cloud resource access'
              />
            }
          >
            <EuiFlexGroup direction='column'>
              {allRoleAssignmentsTableItems.length > 0 && (
                <Fragment>
                  <EuiFlexItem data-test-id='all-role-assignments-table-description'>
                    {memberOrApiKey === 'api-key' ? (
                      <FormattedMessage
                        id='roles-summary.instance-access.all.description.api-key'
                        defaultMessage='This API key provides access to all current and future cloud resources of the following types:'
                      />
                    ) : (
                      <FormattedMessage
                        id='roles-summary.instance-access.all.description.member'
                        defaultMessage='This member has access to all current and future cloud resources of the following types:'
                      />
                    )}
                  </EuiFlexItem>

                  <EuiFlexItem>
                    <AllProjectRoleAssignmentsTable
                      items={allRoleAssignmentsTableItems}
                      isApiKey={memberOrApiKey === 'api-key'}
                    />
                  </EuiFlexItem>

                  {specificRoleAssignments.length > 0 && <EuiSpacer size='xs' />}
                </Fragment>
              )}

              {specificRoleAssignments.length > 0 && (
                <EuiFlexItem>
                  {isAdminconsole ? (
                    <AdminProjectRoleAssignmentsSummary
                      memberOrApiKey={memberOrApiKey}
                      organizationId={organizationId}
                      roleAssignments={specificRoleAssignments}
                    />
                  ) : (
                    <UserProjectRoleAssignmentsSummary
                      memberOrApiKey={memberOrApiKey}
                      roleAssignments={specificRoleAssignments}
                    />
                  )}
                </EuiFlexItem>
              )}
            </EuiFlexGroup>
          </PermissionsRolePanel>
        </EuiFlexItem>
      )}

      {(ccmClusterAllRoleItems.length > 0 || ccmClusterSpecificRoleAssignments.length > 0) && (
        <EuiFlexItem>
          <PermissionsRolePanel
            title={
              <FormattedMessage
                id='roles-summary.title.cloud-connected'
                defaultMessage='Connected cluster access'
              />
            }
          >
            <EuiFlexGroup direction='column'>
              {ccmClusterAllRoleItems.length > 0 && (
                <Fragment>
                  <EuiFlexItem>
                    {memberOrApiKey === 'api-key' ? (
                      <FormattedMessage
                        id='roles-summary.cloud-connected.all.description.api-key'
                        defaultMessage='This API key provides access to all current and future connected clusters of the following types:'
                      />
                    ) : (
                      <FormattedMessage
                        id='roles-summary.cloud-connected.all.description'
                        defaultMessage='This member has access to all current and future connected clusters of the following types:'
                      />
                    )}
                  </EuiFlexItem>

                  <EuiFlexItem>
                    <AllCCMRoleAssignmentsTable items={ccmClusterAllRoleItems} />
                  </EuiFlexItem>
                </Fragment>
              )}

              {ccmClusterSpecificRoleAssignments.length > 0 && (
                <EuiFlexItem>
                  {isAdminconsole ? (
                    <AdminCCMRoleAssignmentsSummary
                      memberOrApiKey={memberOrApiKey}
                      organizationId={organizationId}
                      roleAssignments={ccmClusterSpecificRoleAssignments}
                    />
                  ) : (
                    <UserCCMRoleAssignmentsSummary
                      memberOrApiKey={memberOrApiKey}
                      roleAssignments={ccmClusterSpecificRoleAssignments}
                    />
                  )}
                </EuiFlexItem>
              )}
            </EuiFlexGroup>
          </PermissionsRolePanel>
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  )
}

const getAllCloudResourceRoleAssignmentsTableItems = (
  roleAssignments: RoleAssignments,
  isApiKey?: boolean,
): Array<AllProjectRoleAssignmentsTableItem<ResourceType>> => {
  const deploymentRoleItem: Array<AllProjectRoleAssignmentsTableItem<'deployment'>> = [
    getAllRoleAssignments(normaliseDeploymentRoleAssignments(roleAssignments.deployment)),
  ].flatMap((roleAssignment) => {
    if (roleAssignment.length === 0) {
      return []
    }

    // Build a complete list of roles per project type
    // In theory this array only ever has one element - we cannot assign more than one role for deployments
    const allRoles = roleAssignment.map((role) => role.role_id)
    return {
      resourceType: 'deployment',
      roleIds: allRoles,
    }
  })

  const projectTableItems: Array<AllProjectRoleAssignmentsTableItem<ProjectType>> = [
    'elasticsearch',
    'observability',
    'security',
  ].flatMap((projectType: ProjectType) => {
    const allAssignments = getAllRoleAssignments(
      normaliseProjectRoleAssignments(projectType)(roleAssignments.project?.[projectType]),
    )

    if (allAssignments.length === 0) {
      return []
    }

    const apiAccess = getEsKibanaApiAccess(allAssignments, projectType, isApiKey)

    // Build a complete list of roles per project type
    const allRoles = allAssignments.map((role) => role.role_id)
    return { resourceType: projectType, roleIds: allRoles, apiAccess }
  })

  return [...deploymentRoleItem, ...projectTableItems]
}

const getSpecificCloudResourceRoleAssignments = (
  roleAssignments: RoleAssignments,
): Array<NormalisedResourceRoleAssignment<ResourceType>> => {
  const deploymentRoleAssignments = filterRoleAssignments(
    normaliseDeploymentRoleAssignments(roleAssignments.deployment),
    'specificRoleAssignments',
  )

  const projectRoleAssignments = ['elasticsearch', 'observability', 'security'].flatMap(
    (projectType: ProjectType) =>
      filterRoleAssignments(
        normaliseProjectRoleAssignments(projectType)(roleAssignments.project?.[projectType]),
        'specificRoleAssignments',
      ),
  )

  return [...deploymentRoleAssignments, ...projectRoleAssignments]
}

function getCcmClusterAllRoleItems(
  roleAssignments: RoleAssignments,
): Array<AllProjectRoleAssignmentsTableItem<'cloud-connected'>> {
  const ccmAllRoleAssignments = getAllRoleAssignments(
    normaliseCCMRoleAssignments(roleAssignments.cloud_connected_resource),
  )

  if (ccmAllRoleAssignments.length === 0) {
    return []
  }

  return [
    {
      resourceType: 'cloud-connected',
      roleIds: ccmAllRoleAssignments.map((role) => role.role_id),
    },
  ]
}

export default RolesSummary
